// Custom-app page type — renders an editable, filesystem-backed mini app.
//
// The program's frontend.html runs inside a sandboxed iframe (allow-scripts
// only — no same-origin, so it cannot touch cookies, our DOM, or the API).
// It talks to the host through a tiny postMessage bridge: TimBox.load()/save().
// The host (this component, which IS authenticated) performs the actual
// GET/PUT /programs/:id/data calls and relays the result back.
import { useEffect, useRef, useState } from "react";
import { api } from "../../shared/api/client";
import { toast } from "../../shared/stores/toasts";
import { useAuth } from "../../shared/stores/auth";

type Program = { id: number; name: string; uuid: string | null };

// Injected at the top of every iframe document. Exposes window.TimBox.
const BRIDGE = `<script>
(function(){
  var pending = {}, progress = {}, seq = 0, themeCb = null, backCb = null, backState = false;
  window.TimBox = {
    load: function(cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'load', id:id }, '*'); },
    save: function(data, cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'save', id:id, data:data }, '*'); },
    translate: function(payload, cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'translate', id:id, payload:payload }, '*'); },
    aiModels: function(cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'aiModels', id:id }, '*'); },
    aiChat: function(payload, cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'aiChat', id:id, payload:payload }, '*'); },
    me: function(cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'me', id:id }, '*'); },
    driveFiles: function(payload, cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'driveFiles', id:id, payload:payload }, '*'); },
    driveOpen: function(payload, cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'driveOpen', id:id, payload:payload }, '*'); },
    // 드라이브 올리기(진행률 콜백)·스트리밍 재생 주소·삭제 — 360 뷰어처럼 큰 영상을 다루는 앱용.
    driveUpload: function(payload, cb, onProgress){ var id = ++seq; pending[id] = cb || function(){}; if(onProgress) progress[id] = onProgress; parent.postMessage({ __timbox:1, action:'driveUpload', id:id, payload:payload }, '*'); },
    drivePlayUrl: function(payload, cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'drivePlayUrl', id:id, payload:payload }, '*'); },
    driveDelete: function(payload, cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'driveDelete', id:id, payload:payload }, '*'); },
    docList: function(cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'docList', id:id }, '*'); },
    docRead: function(payload, cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'docRead', id:id, payload:payload }, '*'); },
    docSave: function(payload, cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'docSave', id:id, payload:payload }, '*'); },
    docDelete: function(payload, cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'docDelete', id:id, payload:payload }, '*'); },
    // 메모 동기화 API(/api/v1/memo/*) 전용 통로. 샌드박스 iframe 은 opaque origin 이라
    // 직접 fetch 할 수 없어, 호스트의 인증된 클라이언트를 거쳐 호출한다.
    // payload: { method:'get'|'post'|'delete', path:'/pull?since=0', body?:{} }
    memo: function(payload, cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'memo', id:id, payload:payload }, '*'); },
    // 기타 툴(/api/v1/guitar/*) 전용 통로. 메모 통로와 같은 모양이지만 /guitar 하위만 통과한다.
    guitar: function(payload, cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'guitar', id:id, payload:payload }, '*'); },
    // 지금 테마를 묻는다. 프로그램 페이지는 opaque origin 이라 호스트의 CSS 변수를
    // 직접 읽을 수 없어서, 값을 이 통로로 건네받는다.
    theme: function(cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'theme', id:id }, '*'); },
    // 설치 파일 내려받기. 샌드박스 iframe 은 링크로 파일을 받을 수 없어서
    // 호스트가 인증된 상태로 받아 저장까지 해 준다 (driveOpen 과 같은 방식).
    memoDownload: function(payload, cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'memoDownload', id:id, payload:payload }, '*'); },
    // 메모 모듈의 파일(녹음 원본) 받기. 경로는 호스트가 /rec/<id>/audio 만 통과시킨다.
    memoFile: function(payload, cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'memoFile', id:id, payload:payload }, '*'); },
    // 같은 파일을 저장하지 않고 Blob 으로 받는다 — 메모 안에서 바로 재생할 때
    memoBlob: function(payload, cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'memoBlob', id:id, payload:payload }, '*'); },
    // 테마가 바뀔 때마다 불린다. 창을 다시 열지 않아도 따라 바뀌게.
    onTheme: function(cb){ themeCb = cb; },
    /*
     * 안드로이드 앱의 뒤로가기용.
     *
     * 프로그램 페이지는 iframe 안이라, 앱이 실행하는 자바스크립트(맨 바깥 창)에서는
     * 페이지의 함수가 보이지 않는다. 그래서 페이지가 "지금 닫을 것이 있는지"를
     * 미리 알려 두고(setBack), 앱은 바깥 창에 있는 그 값만 확인한다.
     */
    setBack: function(canBack){ backState = !!canBack; },
    onBack: function(cb){ backCb = cb; },
    /*
     * 바깥 창 주소의 물음표 뒤 값들. 프로그램 페이지는 iframe 이라 자기 주소에는
     * 그 값이 없다 — 위젯이 "이 메모를 열어라"고 보낸 note=... 같은 것을 받는 통로다.
     */
    appParams: function(cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'appParams', id:id }, '*'); },
    // 이 프로그램 폴더의 release/ 에 있는 설치 파일 목록과 내려받기.
    // 샌드박스 iframe 은 링크로 파일을 받을 수 없어 호스트가 인증된 상태로 받아 저장한다.
    releaseList: function(cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'releaseList', id:id }, '*'); },
    releaseDownload: function(payload, cb){ var id = ++seq; pending[id] = cb || function(){}; parent.postMessage({ __timbox:1, action:'releaseDownload', id:id, payload:payload }, '*'); }
  };
  window.addEventListener('message', function(e){
    var m = e.data; if(!m || m.__timbox !== 1) return;
    if(m.action === 'result' && pending[m.id]){ var cb = pending[m.id]; delete pending[m.id]; delete progress[m.id]; cb(m.data); }
    if(m.action === 'progress' && progress[m.id]){ progress[m.id](m.data); }
    if(m.action === 'themeChange' && themeCb){ themeCb(m.data); }
    if(m.action === 'appBack' && backCb){
      // 번호(seq)가 붙어 오면 닫았는지를 그 번호로 답한다. 앱은 이 답을 보고서야 앱을
      // 닫을지 정한다 — 미리 올려 둔 값만 보던 때는 값이 늦어 두 겹이 닫히거나 씹혔다.
      var closed = true;
      try { closed = backCb() !== false; } catch(err) { closed = true; }
      if(m.seq != null){ parent.postMessage({ __timbox:1, action:'appBackDone', seq:m.seq, closed:closed }, '*'); }
    }
  });

  // 바깥 창(호스트)에 페이지 상태를 실어 나른다. 안드로이드는 이 값만 본다.
  window.__stickySetBack = function(v){
    backState = !!v;
    try { parent.postMessage({ __timbox:1, action:'appState', canBack: backState, sync: !!backCb }, '*'); } catch(e) {}
  };
  window.TimBox.setBack = window.__stickySetBack;
})();
<\/script>
`;

const editorBox: React.CSSProperties = {
  width: "100%", boxSizing: "border-box", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
  fontSize: 12, lineHeight: 1.55, padding: 12, border: "1px solid var(--border)", borderRadius: 8,
  background: "var(--bg-0)", color: "var(--text-1)", resize: "vertical", outline: "none", tabSize: 2,
};

/**
 * 지금 테마를 프로그램 페이지가 쓸 수 있는 모양으로 추린다.
 *
 * `applyTheme` 이 CSS 변수를 :root 에 얹어 두므로 거기서 그대로 읽는다.
 * 값을 여기 다시 적으면 테마를 고칠 때 두 곳이 어긋난다.
 */
function readTheme() {
  const s = getComputedStyle(document.documentElement);
  const v = (name: string) => s.getPropertyValue(name).trim();

  return {
    dark: document.documentElement.dataset.dark === "true",
    bg: v("--bg"),
    surface: v("--surface"),
    panel: v("--panel"),
    elev: v("--elev"),
    border: v("--border"),
    text: v("--text"),
    text2: v("--text-2"),
    text3: v("--text-3"),
    primary: v("--p-600"),
    ok: v("--ok"),
    warn: v("--warn"),
    danger: v("--danger"),
  };
}

export default function CustomAppView({ program, admin }: { program: Program; admin: boolean }) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const { user } = useAuth();

  const [frontend, setFrontend] = useState<string>("");
  const [backend,  setBackend]  = useState<string>("");
  const [dir,      setDir]      = useState<string>("");
  const [loading,  setLoading]  = useState(true);

  // Editor state
  const [editing,  setEditing]  = useState(false);
  const [tab,      setTab]      = useState<"frontend" | "backend">("frontend");
  const [feDraft,  setFeDraft]  = useState("");
  const [beDraft,  setBeDraft]  = useState("");
  const [saving,   setSaving]   = useState(false);
  const [reloadKey, setReloadKey] = useState(0);

  // Load the workspace files
  useEffect(() => {
    setLoading(true);
    api.get<{ frontend: string; backend: string; dir: string }>(`/programs/${program.id}/workspace`)
      .then(r => {
        setFrontend(r.data.frontend);
        setBackend(r.data.backend);
        setDir(r.data.dir);
        setFeDraft(r.data.frontend);
        setBeDraft(r.data.backend);
      })
      .catch(() => toast.error("워크스페이스를 불러오지 못했습니다"))
      .finally(() => setLoading(false));
  }, [program.id]);

  // Bridge: relay data load/save requests from the sandboxed iframe.
  useEffect(() => {
    const onMsg = async (e: MessageEvent) => {
      const m = e.data;
      if (!m || m.__timbox !== 1) return;
      if (e.source !== iframeRef.current?.contentWindow) return;

      const reply = (data: unknown) =>
        iframeRef.current?.contentWindow?.postMessage({ __timbox: 1, action: "result", id: m.id, data }, "*");

      if (m.action === "load") {
        try { const r = await api.get(`/programs/${program.id}/data`); reply(r.data); }
        catch { reply({}); }
      } else if (m.action === "save") {
        try { const r = await api.put(`/programs/${program.id}/data`, m.data); reply(r.data); }
        catch { reply(null); }
      } else if (m.action === "translate") {
        try { const r = await api.post(`/ai/translate`, m.payload, { timeout: 120000 }); reply(r.data); }
        catch (err: any) {
          reply({ error: err?.response?.data?.detail || err?.message || "번역 요청 실패" });
        }
      } else if (m.action === "aiModels") {
        try { const r = await api.get(`/ai/translate/models`); reply(r.data); }
        catch { reply([]); }
      } else if (m.action === "aiChat") {
        try { const r = await api.post(`/ai/chat`, m.payload, { timeout: 120000 }); reply(r.data); }
        catch (err: any) {
          reply({ error: err?.response?.data?.error?.message || err?.response?.data?.detail || err?.message || "AI 요청 실패" });
        }
      } else if (m.action === "memo") {
        // /memo 하위로만 통과시킨다 — 임의 API 프록시가 되지 않게 경로를 고정한다.
        const p = m.payload || {};
        const path = String(p.path || "");
        const method = String(p.method || "get").toLowerCase();
        if (!path.startsWith("/") || path.includes("..") || !["get", "post", "delete"].includes(method)) {
          reply({ error: "잘못된 요청" });
        } else {
          try {
            const url = `/memo${path}`;
            // AI 호출은 모델이 답할 때까지 기다려야 한다. 공용 30초로는 Claude CLI 가
            // 늘 시간 초과로 끊긴다. 이 경로에만 넉넉히 준다.
            const cfg = path.startsWith("/ai/") ? { timeout: 180000 } : undefined;
            const r =
              method === "get" ? await api.get(url, cfg)
              : method === "delete" ? await api.delete(url, cfg)
              : await api.post(url, p.body ?? {}, cfg);
            reply(r.data);
          } catch (err: any) {
            reply({ error: err?.response?.data?.detail || err?.message || "메모 요청 실패" });
          }
        }
      } else if (m.action === "guitar") {
        // /guitar 하위로만 통과시킨다 — 임의 API 프록시가 되지 않게 경로를 고정한다.
        const p = m.payload || {};
        const path = String(p.path || "");
        const method = String(p.method || "get").toLowerCase();
        if (!path.startsWith("/") || path.includes("..") || !["get", "post"].includes(method)) {
          reply({ error: "잘못된 요청" });
        } else {
          try {
            // 그림 읽기는 모델이 답할 때까지 기다린다
            const cfg = { timeout: 180000 };
            const r = method === "get" ? await api.get(`/guitar${path}`, cfg) : await api.post(`/guitar${path}`, p.body ?? {}, cfg);
            reply(r.data);
          } catch (err: any) {
            reply({ error: err?.response?.data?.detail || err?.message || "요청 실패" });
          }
        }
      } else if (m.action === "memoBlob") {
        // 녹음 원본을 Blob 으로 넘긴다 (메모 안 재생). postMessage 는 Blob 을 그대로 나른다.
        const p = String((m.payload && m.payload.path) || "");
        if (!/^\/rec\/[A-Za-z0-9_-]+\/audio$/.test(p)) {
          reply({ error: "잘못된 경로" });
        } else {
          try {
            const r = await api.get(`/memo${p}`, { responseType: "blob", timeout: 600000 });
            reply({ ok: true, blob: r.data as Blob });
          } catch (err: any) {
            reply({ error: err?.response?.data?.detail || err?.message || "받기 실패" });
          }
        }
      } else if (m.action === "memoFile") {
        // 녹음 원본 받기. 샌드박스 iframe 은 링크로 파일을 받을 수 없으므로 호스트가
        // 인증된 상태로 받아 저장한다. 경로는 녹음 원본 하나로 고정한다.
        const p = String((m.payload && m.payload.path) || "");
        if (!/^\/rec\/[A-Za-z0-9_-]+\/audio$/.test(p)) {
          reply({ error: "잘못된 경로" });
        } else {
          try {
            const r = await api.get(`/memo${p}`, { responseType: "blob", timeout: 600000 });
            const safe = String((m.payload && m.payload.name) || "recording.m4a").replace(/[^A-Za-z0-9._-]/g, "_");
            const url = URL.createObjectURL(r.data as Blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = safe;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(url), 60000);
            reply({ ok: true, name: safe });
          } catch (err: any) {
            reply({ error: err?.response?.data?.detail || err?.message || "내려받기 실패" });
          }
        }
      } else if (m.action === "memoDownload") {
        // 설치 파일 저장. 샌드박스 iframe 은 링크로 파일을 받을 수 없으므로
        // 호스트가 인증된 상태로 받아 저장까지 한다. 종류는 서버가 정한 것만 통한다.
        const kind = String((m.payload && m.payload.kind) || "");
        if (!["setup", "portable", "apk"].includes(kind)) {
          reply({ error: "알 수 없는 파일" });
        } else {
          try {
            const r = await api.get(`/memo/download/${kind}`, {
              responseType: "blob",
              timeout: 600000,
            });
            const name =
              kind === "apk" ? "Sticky-Android.apk"
              : kind === "portable" ? "Sticky-Portable.exe"
              : "Sticky-Setup.exe";

            const url = URL.createObjectURL(r.data as Blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = name;
            document.body.appendChild(a);
            a.click();
            a.remove();
            // 곧바로 지우면 저장이 시작되기 전에 URL 이 사라지는 브라우저가 있다
            setTimeout(() => URL.revokeObjectURL(url), 60000);

            reply({ ok: true, name });
          } catch (err: any) {
            reply({ error: err?.response?.data?.detail || err?.message || "내려받기 실패" });
          }
        }
      } else if (m.action === "releaseList") {
        try { const r = await api.get(`/programs/${program.id}/release`); reply(r.data); }
        catch { reply({ items: [] }); }
      } else if (m.action === "releaseDownload") {
        // 설치 파일 저장 — 호스트가 인증된 상태로 받아 브라우저에 넘긴다(memoDownload 와 같은 방식).
        const name = String((m.payload && m.payload.name) || "");
        if (!name || name.includes("/") || name.includes("\\")) { reply({ error: "잘못된 파일 이름" }); return; }
        try {
          const r = await api.get(`/programs/${program.id}/release/${encodeURIComponent(name)}`, {
            responseType: "blob",
            timeout: 600000,
          });
          const url = URL.createObjectURL(r.data as Blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = name;
          document.body.appendChild(a);
          a.click();
          a.remove();
          setTimeout(() => URL.revokeObjectURL(url), 60000);
          reply({ ok: true, name });
        } catch (err: any) {
          reply({ error: err?.response?.data?.error?.message || err?.message || "내려받기 실패" });
        }
      } else if (m.action === "appParams") {
        // iframe 은 바깥 창 주소를 못 읽는다. 위젯이 붙여 보낸 값을 그대로 넘긴다.
        const params: Record<string, string> = {};
        new URLSearchParams(window.location.search).forEach((v, k) => { params[k] = v; });
        reply(params);
      } else if (m.action === "appState") {
        // 안드로이드 뒤로가기가 볼 값. 프로그램 페이지는 iframe 안이라 앱이 그 안의
        // 함수를 직접 부를 수 없어서, 상태를 바깥 창에 올려 두고 앱은 이것만 본다.
        (window as any).StickyApp = {
          canBack: () => Boolean((window as any).__stickyCanBack),
          back: () => {
            iframeRef.current?.contentWindow?.postMessage(
              { __timbox: 1, action: "appBack" },
              "*"
            );
            return true;
          },
          /*
           * 새 방식: 앱이 번호를 붙여 묻고, 페이지가 실제로 닫았는지를 그 번호로
           * 답한다(appBackDone → StickyNative.backResult). 미리 올려 둔 값(canBack)은
           * 한 박자 늦을 수 있어, 빠르게 두 번 누르면 두 겹이 닫히거나 씹혔다.
           * 페이지가 이 방식을 모르면 false — 앱은 예전 방식으로 간다.
           */
          ask: (seq: number) => {
            if (!(window as any).__stickyBackSync) return false;
            const target = iframeRef.current?.contentWindow;
            if (!target) return false;
            target.postMessage({ __timbox: 1, action: "appBack", seq }, "*");
            return true;
          },
        };
        (window as any).__stickyCanBack = Boolean(m.canBack);
        (window as any).__stickyBackSync = Boolean(m.sync);
      } else if (m.action === "appBackDone") {
        // 안드로이드 앱에 답을 전한다. 앱이 아니면(브라우저) 받을 곳이 없으니 그냥 둔다.
        // 메모 앱은 StickyNative, 그 밖의 앱 껍데기(기타 툴 등)는 TimBoxNative 로 받는다
        const native = (window as any).TimBoxNative || (window as any).StickyNative;
        if (native && typeof native.backResult === "function") {
          native.backResult(Number(m.seq), Boolean(m.closed));
        }
      } else if (m.action === "theme") {
        reply(readTheme());
      } else if (m.action === "me") {
        reply({ id: user?.id ?? null, username: user?.username ?? null, name: user?.name ?? null, roles: user?.roles ?? [], admin });
      } else if (m.action === "driveFiles") {
        // Permission-filtered file list by scope. The backend enforces can_access_file
        // against the authenticated user, so the iframe only ever receives files the
        // user is allowed to see — the sandbox never gets the token or direct API access.
        try {
          const scope = (m.payload && m.payload.scope) || "PERSONAL";
          const r = await api.get(`/drive/files`, { params: { scope } });
          reply(r.data);
        } catch { reply([]); }
      } else if (m.action === "driveOpen") {
        // Open/preview a drive file. The parent (authenticated) fetches the bytes and
        // opens a blob URL, so the sandboxed iframe never touches auth or the raw URL.
        try {
          const fid = m.payload && m.payload.fileId;
          if (!fid) { reply({ error: "fileId 없음" }); return; }
          let blob: Blob;
          try {
            const r = await api.get(`/drive/files/${fid}/preview`, { responseType: "blob" });
            blob = r.data as Blob;
          } catch {
            const r = await api.get(`/drive/files/${fid}/download`, { responseType: "blob" });
            blob = r.data as Blob;
          }
          const url = URL.createObjectURL(blob);
          window.open(url, "_blank", "noopener");
          setTimeout(() => URL.revokeObjectURL(url), 60000);
          reply({ ok: true });
        } catch { reply({ error: "파일을 열 수 없습니다" }); }
      } else if (m.action === "driveUpload") {
        // 샌드박스 페이지가 고른 파일을 호스트가 인증된 상태로 드라이브에 올린다(postMessage 는 File 을 그대로 나른다).
        // 큰 영상이라 시간 제한을 풀고 진행률을 따로 흘려 보낸다.
        const p = m.payload || {};
        const file = p.file as File | undefined;
        if (!(file instanceof Blob)) { reply({ error: "파일 없음" }); return; }
        try {
          const fd = new FormData();
          fd.append("file", file, String(p.name || file.name || "video"));
          fd.append("scope", String(p.scope || "PERSONAL"));
          if (p.folder_id != null) fd.append("folder_id", String(p.folder_id));
          const r = await api.post(`/drive/files`, fd, {
            timeout: 0,
            onUploadProgress: ev => iframeRef.current?.contentWindow?.postMessage(
              { __timbox: 1, action: "progress", id: m.id, data: { loaded: ev.loaded, total: ev.total ?? file.size } }, "*"),
          });
          reply(r.data);
        } catch (err: any) { reply({ error: err?.response?.data?.detail || err?.message || "업로드 실패" }); }
      } else if (m.action === "drivePlayUrl") {
        // 파일 하나에만 통하는 재생 토큰으로 스트리밍 주소를 만든다 — 로그인 토큰은 여전히 iframe 에 가지 않는다.
        try {
          const fid = Number(m.payload && m.payload.fileId);
          const r = await api.get(`/drive/files/${fid}/play-token`);
          const base = api.defaults.baseURL || "/api/v1";
          const origin = /^https?:/.test(base) ? "" : location.origin;
          reply({ url: `${origin}${base}/drive/files/${fid}/play?t=${encodeURIComponent(r.data.token)}` });
        } catch (err: any) { reply({ error: err?.response?.data?.detail || "재생 주소를 받지 못했습니다" }); }
      } else if (m.action === "driveDelete") {
        try { await api.delete(`/drive/files/${Number(m.payload && m.payload.fileId)}`); reply({ ok: true }); }
        catch (err: any) { reply({ error: err?.response?.data?.detail || "삭제 실패" }); }
      } else if (m.action === "docList") {
        // List named documents (e.g. *.emm) in the program's docs/ folder.
        try { const r = await api.get(`/programs/${program.id}/docs`); reply(r.data); }
        catch { reply([]); }
      } else if (m.action === "docRead") {
        try {
          const r = await api.get(`/programs/${program.id}/docs/${encodeURIComponent(m.payload.name)}`);
          reply(r.data);
        } catch { reply(null); }
      } else if (m.action === "docSave") {
        try {
          const r = await api.put(`/programs/${program.id}/docs/${encodeURIComponent(m.payload.name)}`, { content: m.payload.content });
          reply(r.data);
        } catch { reply({ error: "저장 실패" }); }
      } else if (m.action === "docDelete") {
        try {
          await api.delete(`/programs/${program.id}/docs/${encodeURIComponent(m.payload.name)}`);
          reply({ ok: true });
        } catch { reply({ error: "삭제 실패" }); }
      }
    };
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [program.id, user, admin]);

  // 테마가 바뀌면 프로그램 페이지에도 알린다. `applyTheme` 이 :root 의 data-dark 를
  // 바꾸는 것을 신호로 삼는다 — 테마 스토어에 손대지 않고 붙일 수 있는 지점이다.
  useEffect(() => {
    const notify = () =>
      iframeRef.current?.contentWindow?.postMessage(
        { __timbox: 1, action: "themeChange", data: readTheme() },
        "*"
      );

    const observer = new MutationObserver(notify);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-dark", "style"],
    });
    return () => observer.disconnect();
  }, []);

  const saveFrontend = async () => {
    setSaving(true);
    try {
      const r = await api.put<{ frontend: string }>(`/programs/${program.id}/workspace/frontend`, { content: feDraft });
      setFrontend(r.data.frontend);
      setReloadKey(k => k + 1);
      toast.success("프론트엔드 저장됨");
    } catch (e: any) { toast.error("저장 실패", e?.response?.data?.error?.message ?? ""); }
    finally { setSaving(false); }
  };

  const saveBackend = async () => {
    // client-side JSON check for a friendlier error
    try { JSON.parse(beDraft); }
    catch (err: any) { toast.error("JSON 형식 오류", String(err?.message ?? "")); return; }
    setSaving(true);
    try {
      const r = await api.put<{ backend: string }>(`/programs/${program.id}/workspace/backend`, { content: beDraft });
      setBackend(r.data.backend);
      toast.success("백엔드 설정 저장됨");
    } catch (e: any) { toast.error("저장 실패", e?.response?.data?.error?.message ?? ""); }
    finally { setSaving(false); }
  };

  if (loading) {
    return <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", color: "var(--text-3)", fontSize: 13 }}>워크스페이스 불러오는 중…</div>;
  }

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      {/* Toolbar */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "8px 24px", borderBottom: "1px solid var(--border)", flexShrink: 0, background: "var(--bg-1)" }}>
        {dir && (
          <span title="로컬 폴더 경로" style={{ fontSize: 11, color: "var(--text-3)", fontFamily: "ui-monospace, monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", flex: 1 }}>
            📂 {dir}
          </span>
        )}
        {admin && (
          <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
            <button
              onClick={() => setEditing(false)}
              style={tabBtn(!editing)}
            >👁 미리보기</button>
            <button
              onClick={() => { setFeDraft(frontend); setBeDraft(backend); setEditing(true); }}
              style={tabBtn(editing)}
            >✏️ 편집</button>
          </div>
        )}
      </div>

      {/* Body */}
      {editing && admin ? (
        <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, overflow: "hidden" }}>
          {/* file tabs */}
          <div style={{ display: "flex", gap: 0, padding: "8px 24px 0", flexShrink: 0 }}>
            <button onClick={() => setTab("frontend")} style={fileTab(tab === "frontend")}>🖥 frontend.html</button>
            <button onClick={() => setTab("backend")}  style={fileTab(tab === "backend")}>⚙️ backend.json</button>
          </div>

          <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0, padding: "10px 24px 16px", gap: 10 }}>
            {tab === "frontend" ? (
              <>
                <div style={{ fontSize: 11, color: "var(--text-3)" }}>
                  HTML/CSS/JS를 작성하세요. <code>TimBox.load(cb)</code> / <code>TimBox.save(obj)</code> 로 데이터를 읽고 쓸 수 있습니다.
                </div>
                <textarea
                  value={feDraft}
                  onChange={e => setFeDraft(e.target.value)}
                  spellCheck={false}
                  style={{ ...editorBox, flex: 1, minHeight: 200 }}
                />
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button className="t-btn t-btn-primary" onClick={saveFrontend} disabled={saving || feDraft === frontend}>
                    {saving ? "저장 중…" : "프론트엔드 저장"}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontSize: 11, color: "var(--text-3)" }}>
                  앱 설정을 JSON으로 저장합니다. (런타임 데이터는 <code>data.json</code> 에 자동 저장됩니다.)
                </div>
                <textarea
                  value={beDraft}
                  onChange={e => setBeDraft(e.target.value)}
                  spellCheck={false}
                  style={{ ...editorBox, flex: 1, minHeight: 200 }}
                />
                <div style={{ display: "flex", justifyContent: "flex-end" }}>
                  <button className="t-btn t-btn-primary" onClick={saveBackend} disabled={saving || beDraft === backend}>
                    {saving ? "저장 중…" : "백엔드 설정 저장"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        // Preview — sandboxed iframe
        <iframe
          key={reloadKey}
          ref={iframeRef}
          title={program.name}
          sandbox="allow-scripts allow-popups allow-popups-to-escape-sandbox allow-downloads"
          // 프로그램 페이지가 스스로 전체화면이 될 수 있게 한다 (발표 타이머의 표시 모드처럼
          // 관객에게 보여 주는 화면이 필요한 앱들). 권한 정책과 속성 둘 다 있어야 동작한다.
          // 마이크는 기타 튜너처럼 소리를 듣는 앱용. 샌드박스 문서는 opaque origin 이라 '*' 로
          // 넘겨야 닿는다. 실제로 켤 때는 브라우저가 사람에게 한 번 더 묻는다.
          // 위치는 360 뷰어가 촬영 지점을 지도에 찍을 때 쓴다.
          allow="fullscreen; microphone *; autoplay *; geolocation *"
          allowFullScreen
          srcDoc={BRIDGE + frontend}
          style={{ flex: 1, width: "100%", border: "none", background: "#fff" }}
        />
      )}
    </div>
  );
}

function tabBtn(active: boolean): React.CSSProperties {
  return {
    fontSize: 12, padding: "4px 12px", borderRadius: 6, cursor: "pointer",
    border: `1px solid ${active ? "var(--p-400)" : "var(--border)"}`,
    background: active ? "rgba(99,102,241,0.1)" : "var(--bg-0)",
    color: active ? "var(--p-600)" : "var(--text-2)", fontWeight: active ? 600 : 400,
  };
}

function fileTab(active: boolean): React.CSSProperties {
  return {
    fontSize: 12, padding: "6px 14px", cursor: "pointer", fontFamily: "ui-monospace, monospace",
    border: "1px solid var(--border)", borderBottom: active ? "1px solid var(--bg-0)" : "1px solid var(--border)",
    borderRadius: "7px 7px 0 0", marginBottom: -1,
    background: active ? "var(--bg-0)" : "transparent",
    color: active ? "var(--text-1)" : "var(--text-3)", fontWeight: active ? 600 : 400,
  };
}
